import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../../../lib/api";
import { initialCfg } from "./_genCommon";
import ConfigureStep from "./ConfigureStep";
import GenerateStep from "./GenerateStep";
import CompareStep from "./CompareStep";
import AlternativeFullView from "./AlternativeFullView";
import HybridBuilder from "./HybridBuilder";
import SideBySideCompare from "./SideBySideCompare";

/**
 * Generative Design — top-level orchestrator.
 * Configure → Generate → Compare with launchable Full-View / Hybrid /
 * Side-by-side overlays.
 */
export default function GenerativeDesignPanel({ projectId }) {
  const [step, setStep] = useState("configure");
  const [domain, setDomain] = useState("process_plant");
  const [cfg, setCfg] = useState(initialCfg("process_plant"));
  const [job, setJob] = useState(null);
  const [alts, setAlts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [activeAlt, setActiveAlt] = useState(null);
  const [reportMd, setReportMd] = useState("");
  const [err, setErr] = useState("");
  // Overlays
  const [showFullView, setShowFullView] = useState(null);
  const [showHybrid, setShowHybrid] = useState(false);
  const [showSxs, setShowSxs] = useState(false);

  // Restore latest job on mount
  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${projectId}/generative/alternatives`);
      if (r.data?.alternatives?.length) {
        setAlts(r.data.alternatives);
        const d = r.data.job?.config?.domain;
        if (d) setDomain(d);
        setStep("compare");
      }
    } catch (_) { /* ok */ }
  }, [projectId]);
  useEffect(() => { reload(); }, [reload]);

  const onDomainChange = (d) => { setDomain(d); setCfg(initialCfg(d)); };

  const cancelJob = async () => {
    if (!job?.id) return;
    try {
      await api.post(`/projects/${projectId}/generative/cancel/${job.id}`);
      setStep("configure");
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const downloadPdf = async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/generative/report.pdf`;
      const tok = localStorage.getItem("rachana_token");
      const r = await fetch(url, { headers: tok ? { Authorization: `Bearer ${tok}` } : {} });
      if (!r.ok) throw new Error("PDF generation failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `generative-report-${projectId.slice(0, 8)}.pdf`;
      a.click();
    } catch (e) { setErr(String(e)); }
  };

  const startGen = async () => {
    setErr(""); setStep("generating"); setAlts([]);
    try {
      const body = { ...cfg, domain };
      const r = await api.post(`/projects/${projectId}/generative/generate`, body);
      const jid = r.data.job_id;
      const tick = async () => {
        try {
          const sr = await api.get(`/projects/${projectId}/generative/status/${jid}`);
          setJob({ ...sr.data, id: jid });
          try {
            const lr = await api.get(`/projects/${projectId}/generative/alternatives?job_id=${jid}`);
            if (lr.data?.alternatives?.length) setAlts(lr.data.alternatives);
          } catch (_) { /* ok */ }
          if (sr.data.status === "done") {
            const lr = await api.get(`/projects/${projectId}/generative/alternatives?job_id=${jid}`);
            setAlts(lr.data.alternatives || []);
            setStep("compare");
          } else if (sr.data.status === "failed") {
            setErr(sr.data.error || "Generation failed"); setStep("configure");
          } else if (sr.data.status === "cancelled") {
            setStep("configure");
          } else {
            setTimeout(tick, 600);
          }
        } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
      };
      tick();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || String(e));
      setStep("configure");
    }
  };

  const select = async (altId) => {
    try {
      await api.post(`/projects/${projectId}/generative/select/${altId}`);
      setSelected(altId);
      // toast
      // eslint-disable-next-line no-alert
      alert(`Selected ${altId} as project base design.\nIt's now in metadata.base_design.`);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const loadReport = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/generative/report`);
      setReportMd(r.data.markdown);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const onHybridApplied = (data) => {
    if (data?.alternatives) setAlts(data.alternatives);
  };

  return (
    <div data-testid="generative-design" style={{ padding: 16,
                          height: "calc(100vh - 280px)", minHeight: 700,
                          overflow: "auto" }}>
      {/* Stepper */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[
          { k: "configure", l: "1 · Configure constraints" },
          { k: "generating", l: "2 · Generate" },
          { k: "compare", l: "3 · Compare + Select" },
        ].map(s => (
          <div key={s.k} style={{ padding: "6px 14px", borderRadius: 4, fontSize: 11,
                                              background: step === s.k ? "#F26B1F" : "var(--pf-bg)",
                                              color: step === s.k ? "#0e0e10" : "var(--pf-muted)",
                                              border: "1px solid var(--pf-border)" }}>
            {s.l}
          </div>
        ))}
      </div>

      {step === "configure" && (
        <ConfigureStep domain={domain} cfg={cfg}
              onDomainChange={onDomainChange} onCfgChange={setCfg}
              onGenerate={startGen} />
      )}
      {step === "generating" && (
        <GenerateStep job={job} alts={alts} onCancel={cancelJob} />
      )}
      {step === "compare" && (
        <CompareStep alts={alts} weights={cfg.objective_weights}
              onSelect={select} selected={selected}
              activeAlt={activeAlt} setActiveAlt={setActiveAlt}
              loadReport={loadReport} reportMd={reportMd}
              onBack={() => setStep("configure")}
              projectId={projectId}
              onAltsRefreshed={setAlts}
              budgetMax={cfg.budget_max_INR_cr}
              monthsMax={cfg.max_construction_months}
              onDownloadPdf={downloadPdf}
              onOpenFullView={(a) => setShowFullView(a)}
              onOpenHybrid={() => setShowHybrid(true)}
              onOpenSxs={() => setShowSxs(true)} />
      )}

      {/* Overlays */}
      {showFullView && (
        <AlternativeFullView projectId={projectId} alt={showFullView}
              onClose={() => setShowFullView(null)}
              onAdopt={(id) => { select(id); setShowFullView(null); }}
              isAdopted={selected === showFullView.id} />
      )}
      {showHybrid && (
        <HybridBuilder projectId={projectId} alts={alts}
              onApplied={onHybridApplied}
              onClose={() => setShowHybrid(false)} />
      )}
      {showSxs && (
        <SideBySideCompare projectId={projectId} alts={alts}
              onClose={() => setShowSxs(false)} />
      )}

      {err && <div data-testid="gen-error" style={{ marginTop: 16, color: "#FF8888" }}>
        {err}
      </div>}
    </div>
  );
}
